import { Modal } from '../../ui/Modal'
import type { Feature } from './features'

/**
 * One feature, as far as it goes.
 *
 * A reading pane, not a decision — which is a deliberate exception to the app's
 * own rule that a modal holds a decision and a sheet holds a record (see
 * `40-design-system.md`). This holds neither: the marketing surface is not
 * the back office, there is no list behind it to keep visible and nothing to
 * commit, so the shape that fits is "the page, with one thing opened up".
 *
 * Its job is to answer the doubt the card cannot. A feature list makes every
 * product look alike, because at one line each they all cover the same nouns;
 * what separates them is whether they have met the awkward case you are
 * thinking of. So the panel leads with the outcome, shows the thing running,
 * and then names those cases in the form a gabbai would recognize them.
 *
 * The panel closes on the reassurance, not the depth. Depth without it reads as
 * a warning about how complicated this is going to be.
 */
export function FeatureDetail({
  feature,
  onClose,
}: {
  feature: Feature
  onClose: () => void
}) {
  const { promise, shots, depth, simple } = feature.detail
  return (
    <Modal title={feature.title} onClose={onClose} size="wide">
      <div className="mk-detail">
        <p className="mk-detail__promise">{promise}</p>

        {/* Keyed by each shot's own key, which `features.tsx` assigns — the list
            is identified by what a shot IS, not by where it sits in the array. */}
        {shots.map((shot) => (
          <div className="mk-detail__shot" key={shot.key}>
            {shot}
          </div>
        ))}

        <div className="mk-detail__depth">
          <p className="mk-detail__label">Things it already knows about</p>
          <ul className="mk-detail__list">
            {depth.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        </div>

        <p className="mk-detail__simple">{simple}</p>
      </div>
    </Modal>
  )
}
